import React, { useState } from 'react';

const VideoCarousel = ({ videos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const prevVideo = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? videos.length - 1 : prevIndex - 1));
  };

  const nextVideo = () => {
    setCurrentIndex((prevIndex) => (prevIndex === videos.length - 1 ? 0 : prevIndex + 1));
  };

  const isTikTokUrl = (url) => {
    return url && (url.includes('tiktok.com') || url.includes('tiktokv.com'));
  };

  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  const getTikTokEmbedUrl = (url) => {
    // Extract video ID from URL
    const match = url.match(/video\/(\d+)/);
    const videoId = match ? match[1] : null;
    return videoId ? `https://www.tiktok.com/embed/v2/${videoId}` : url;
  };

  const getYouTubeEmbedUrl = (url) => {
    let videoId = null;
    if (url.includes('youtu.be')) {
        videoId = url.split('/').pop().split('?')[0];
    } else if (url.includes('youtube.com')) {
        const urlParams = new URL(url).searchParams;
        videoId = urlParams.get('v');
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  if (!videos || videos.length === 0) {
    return <div className="text-center text-gray-500">No videos available</div>;
  }

  const currentVideo = videos[currentIndex];
  const isTikTok = isTikTokUrl(currentVideo.src);
  const isYouTube = isYouTubeUrl(currentVideo.src);

  return (
    <div className="relative w-full max-w-3xl mx-auto"> {/* Max width increased for better visibility */}
      <div className={`relative bg-black rounded-lg overflow-hidden shadow-2xl ${isTikTok ? 'aspect-[9/16]' : 'aspect-video'}`}>
        {isTikTok ? (
            <iframe
            key={currentVideo.src}
            src={getTikTokEmbedUrl(currentVideo.src)}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={currentVideo.title}
          ></iframe>
        ) : isYouTube ? (
            <iframe
            key={currentVideo.src}
            src={getYouTubeEmbedUrl(currentVideo.src)}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={currentVideo.title}
          ></iframe>
        ) : (
            <video
            key={currentVideo.src}
            className="w-full h-full object-contain"
            controls
            autoPlay={false}
            poster={currentVideo.poster}
            >
            <source src={currentVideo.src} type="video/mp4" />
            Your browser does not support the video tag.
            </video>
        )}
        
        {/* Caption/Title Overlay - Only show for non-TikToks/non-YouTube to avoid blocking controls */}
        {!isTikTok && !isYouTube && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-4">
            <h3 className="text-lg font-bold">{currentVideo.title}</h3>
            <p className="text-sm text-gray-300">{currentVideo.description}</p>
            </div>
        )}
      </div>

      {/* Navigation Buttons - Moved outside for better visibility with TikToks */}
      <button
        onClick={prevVideo}
        className="absolute top-1/2 -left-12 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-700 transition focus:outline-none"
        aria-label="Previous Video"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextVideo}
        className="absolute top-1/2 -right-12 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-700 transition focus:outline-none"
        aria-label="Next Video"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center space-x-2 mt-4">
        {videos.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition ${
              index === currentIndex ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            aria-label={`Go to video ${index + 1}`}
          />
        ))}
      </div>
       {/* External Info for current video */}
        <div className="text-center mt-4">
             <h3 className="text-lg font-bold text-gray-900 dark:text-white">{currentVideo.title}</h3>
             <p className="text-gray-600 dark:text-gray-400">{currentVideo.description}</p>
        </div>
    </div>
  );
};

export default VideoCarousel;
